import React, { useContext, useMemo } from 'react';

import { Color } from '@/constants/color';
import { useDrugScreenPreview } from '@/hooks/useDrugScreens';
import PersonContext from '@/pages/Person/PersonContext';
import Alert from '@/ui/components/Alert';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import DataList, { DataRow } from '@/ui/components/DataList';
import Modal from '@/ui/components/Modal';
import Spinner from '@/ui/components/Spinner';
import { currencyInCentsFormatter } from '@/utils/currencies';
import drugScreenCompliantResult from '@/utils/drugScreenCompliantResult';

import type { Props } from './types';

const DrugScreenPayoutPreview = ({ drugScreenId, drugScreenPayload, onConfirm }: Props) => {
  const {
    person: { episode_of_care, targeted_substances }
  } = useContext(PersonContext);
  const {
    data: payoutPreviewResponse,
    error,
    isError,
    isPending,
    refetch
  } = useDrugScreenPreview({ drugScreenId, values: drugScreenPayload });

  const compliantResult = useMemo(() => {
    if (!episode_of_care?.program.drug_category) {
      return 'N/A (No program)';
    }

    return drugScreenCompliantResult(episode_of_care.program.drug_category);
  }, [episode_of_care?.program.drug_category]);

  const handleConfirm = () => {
    onConfirm(true);
  };

  const handleClose = () => {
    onConfirm(false);
  };

  const renderPayoutInfo = useMemo(() => {
    if (payoutPreviewResponse?.data) {
      const { base, bonus, is_compliant, payout_in_cents, test_administration } =
        payoutPreviewResponse.data;

      if (payout_in_cents || payout_in_cents === 0) {
        const greenTextClasses = 'text-green-700 text-2xl font-bold';

        return (
          <div>
            <Card>
              <div className="mb-2">
                <DataList>
                  <DataRow label="Test Administration">
                    <span className={greenTextClasses}>
                      {currencyInCentsFormatter(test_administration)}
                    </span>
                  </DataRow>

                  <DataRow label="+ Reward for Desired Result">
                    <span className={greenTextClasses}>{currencyInCentsFormatter(base)}</span>
                  </DataRow>

                  <DataRow label="+ Streak Bonus">
                    <span className={greenTextClasses}>{currencyInCentsFormatter(bonus)}</span>
                  </DataRow>
                </DataList>
              </div>

              <Alert level="info">
                Saving this drug screen will result in a {currencyInCentsFormatter(payout_in_cents)}{' '}
                reward for the member
              </Alert>
            </Card>

            <Card>
              <DataList>
                <DataRow label="Program">{episode_of_care?.program.name || 'N/A'}</DataRow>

                <DataRow label="Target Substance for CM">
                  {targeted_substances?.join(', ') || 'N/A'}
                </DataRow>

                <DataRow label="Desired Result for CM">{compliantResult}</DataRow>

                <DataRow label="Desired Result Met">{is_compliant ? '✅' : '❌'}</DataRow>
              </DataList>
            </Card>
          </div>
        );
      }
    }

    return <div>Drug screen payout not available</div>;
  }, [compliantResult, episode_of_care, payoutPreviewResponse?.data, targeted_substances]);

  return (
    <div>
      <Modal
        dismissButton={{
          color: Color.WHITE,
          children: 'Cancel'
        }}
        button={{
          onClick: handleConfirm,
          children: 'Continue & Save'
        }}
        onClose={handleClose}
        open={true}
        textContent={
          isPending ? (
            <Spinner label="Loading drug screen payout" />
          ) : isError ? (
            <div>
              <Alert level="error">
                <div>{error?.message}</div>
                <Button color={Color.TEAL} onClick={() => refetch()} size="xs">
                  Retry
                </Button>
              </Alert>
            </div>
          ) : (
            renderPayoutInfo
          )
        }
        title="Drug Screen Reward Preview"
      />
    </div>
  );
};
export default React.memo(DrugScreenPayoutPreview);
